import { Group, Modal, ScrollArea, Stack, UnstyledButton } from '@mantine/core';
import type { AiTaskEmbeddedPromptOut } from '@repo/api-codegen';
import { Icon, Text, TextSkeleton } from '@repo/foundations';
import { useNavigate } from 'react-router';
import { RUN_STATUS } from '../../../pages/AIAgentsPage/constants';
import { formatDateTimeShort } from '../../../utils/time';
import type { AgentRunHistoryModalProps } from '../types';

export function StatusIcon({ status }: { status: string }) {
	let name: Parameters<typeof Icon>[0]['name'] = 'circle';
	let fillColor: Parameters<typeof Icon>[0]['fillColor'] | undefined =
		undefined;

	if (status === RUN_STATUS.SUCCESS || status === RUN_STATUS.COMPLETED) {
		name = 'circleCheckFilled';
		fillColor = 'icon/success/default';
	} else if (status === RUN_STATUS.ERROR || status === RUN_STATUS.FAILED) {
		name = 'alertCircleFilled';
		fillColor = 'icon/critical/default';
	} else if (
		status === RUN_STATUS.IN_PROGRESS ||
		status === RUN_STATUS.RUNNING
	) {
		name = 'playerPlay';
	} else if (status === RUN_STATUS.PENDING) {
		name = 'clock';
	}

	return <Icon name={name} fillColor={fillColor} />;
}

function RunRow({
	run,
	onOpenChat,
	agentTitle,
}: {
	run: AiTaskEmbeddedPromptOut;
	onOpenChat: (promptId: string) => void;
	agentTitle?: string;
}) {
	const title: string = agentTitle ?? 'Agent run';
	const createdAtFormatted: string = formatDateTimeShort(run.created_at);
	const promptId = run.embedded_prompt?.id;

	return (
		<Group position="apart" noWrap py="xs">
			<UnstyledButton
				w="100%"
				onClick={() => {
					if (promptId) onOpenChat(promptId);
				}}
			>
				<Group position="apart" noWrap w="100%">
					<Group spacing="2xs" noWrap>
						<StatusIcon status={run.status} />
						<Text size="sm" truncate maw={300}>
							{title}
						</Text>
					</Group>
					<Group spacing="2xs" noWrap>
						<Text size="sm" c="text/primary/subdued">
							{createdAtFormatted}
						</Text>
					</Group>
				</Group>
			</UnstyledButton>
		</Group>
	);
}

export function AgentRunHistoryModal({
	opened,
	onClose,
	runs,
	isLoading,
	agentTitle,
}: AgentRunHistoryModalProps) {
	const navigate = useNavigate();
	const handleOpenChat = (promptId: string) => navigate(`/ai/${promptId}`);
	return (
		<Modal opened={opened} onClose={onClose} title="Run history" size={480}>
			<ScrollArea.Autosize mah={216}>
				<Stack spacing={0}>
					{isLoading && (
						<Stack spacing="xs" px="sm" py="xs">
							<TextSkeleton height={14} width="60%" />
							<TextSkeleton height={14} width="48%" />
							<TextSkeleton height={14} width="72%" />
						</Stack>
					)}
					{!isLoading && !runs?.length && (
						<Stack align="center" py="xl" spacing="sm">
							<Text size="lg">⚪</Text>
							<Text size="md" fw="medium" color="text/primary/default">
								No runs yet
							</Text>
							<Text size="sm" color="text/primary/default" ta="center">
								This agent hasn&apos;t been executed. Click &quot;Run now&quot;
								to start your first run.
							</Text>
						</Stack>
					)}
					{!isLoading &&
						(runs ?? []).map((run) => (
							<RunRow
								key={run.id}
								run={run}
								onOpenChat={handleOpenChat}
								agentTitle={agentTitle}
							/>
						))}
				</Stack>
			</ScrollArea.Autosize>
		</Modal>
	);
}
